define((require) => {
    "use strict";

    const _               = require("underscore");
    const Marionette      = require("marionette");
    const WidgetScaleMode = require("shared/ui/experience/widgets/WidgetScaleMode");

    /**
     * Renders and propagates each widget view and state from a composite widget collection
     *
     * @return {Marionette CollectionView}
     */
    return Marionette.CollectionView.extend({

        className: "widget-container-inner",

        _experience: null,
        _compositeModel: null,

        initialize() {
            this._compositeModel = this.options.compositeModel;
            this._experience = this.options.viewModel.get("experience");
        },

        /**
         * Get the view for each widget, this overwrites the default from marionette
         *
         * @param {Backbone.Model} widgetModel
         * @returns {Function(WidgetView)} Returns widget view constructor function
         */
        getItemView(widgetModel) {
            const type = widgetModel.get("type");
            const ViewClass = this.options.WidgetTypes.lookup(type).viewClass;

            if (this.options.edited) {
                return this.options.TransformableWidgetFactory.create(type, ViewClass);
            }

            return ViewClass;
        },

        itemViewOptions() {
            return {
                viewModel: this.options.viewModel,
                edited: this.options.edited,
                container: this.$el,
                enableActions: this.options.enableActions,
                enableAnimations: this.options.enableAnimations,
                getPositionOffset: () => (
                    {
                        left: (this._compositeModel.get("left") * this._experience.get("scaledWidth")) / 100,
                        top: (this._compositeModel.get("top") * this._experience.get("scaledHeight")) / 100
                    }
                )
            };
        },

        updateGroupActiveState(active) {
            if (active === false) {
                this.children.each((itemView) => {
                    itemView.setActive(false);
                });

                return;
            }

            // If group widgets are visible make them active as well
            const visiblePartOfExperience = this.options.viewModel.get("viewportVisibility");
            this.children.each((itemView) => {
                const widgetRectangle = itemView.getRect();
                const widgetIsVisible = visiblePartOfExperience.intersects(widgetRectangle);
                itemView.setActive(widgetIsVisible);
            });
        },

        /**
         * Calculate the bounding box of all widgets in this collection
         *
         * @returns {left, top, width, height} Returns the position and size of the bounding box
         */
        getGroupBoundingBox() {
            const scale = this._experience.get("scale");
            const topLeftPoint = {x: Infinity, y: Infinity};
            const bottomRightPoint = {x: 1, y: 1};

            this.children.each((itemView) => {
                const boundingBox = itemView.getBoundingBox();

                if (itemView.model.get("scaleMode") === WidgetScaleMode.STRETCH) {
                    // Revert the stretch compensation (see AbstractWidgetView #getRect) so
                    // we get the initial values instead of one for positioning
                    const xf = boundingBox.width - (boundingBox.width * scale.x);
                    const yf = boundingBox.height - (boundingBox.height * scale.y);
                    boundingBox.x += xf * 0.5;
                    boundingBox.y += yf * 0.5;

                    // Take scaling into account for scaled values
                    boundingBox.width *= scale.x;
                    boundingBox.height *= scale.y;
                }

                const x1 = boundingBox.x;
                const y1 = boundingBox.y;
                const x2 = boundingBox.x + boundingBox.width;
                const y2 = boundingBox.y + boundingBox.height;

                if (itemView.model.get("isVisible")) {
                    topLeftPoint.x = _.min([x1, topLeftPoint.x]);
                    topLeftPoint.y = _.min([y1, topLeftPoint.y]);
                    bottomRightPoint.x = _.max([x2, bottomRightPoint.x]);
                    bottomRightPoint.y = _.max([y2, bottomRightPoint.y]);
                }
            });

            return {
                left: (topLeftPoint.x / this._experience.get("scaledWidth")) * 100,
                top: (topLeftPoint.y / this._experience.get("scaledHeight")) * 100,
                width: ((bottomRightPoint.x - topLeftPoint.x) / this._experience.get("scaledWidth")) * 100,
                height: ((bottomRightPoint.y - topLeftPoint.y) / this._experience.get("scaledHeight")) * 100
            };
        }

    }, {
        injection: {
            parameters: ["WidgetTypes"],
            asOptions: true
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/compositeWidget/CompositeWidgetCollectionView.js